import React from "react";
import { Button, IconButton, Snackbar, Tooltip } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { claudePrompt, type AiContext } from "./context";
import ClaudeSetup from "./ClaudeSetup";

type Copied = { severity: "success" | "error"; message: string };

type ClaudeActions = { copyPrompt: (context: AiContext) => void; openSetup: () => void };

const ClaudeCtx = React.createContext<ClaudeActions | null>(null);

export const ClaudePromptProvider = ({ children }: { children: React.ReactNode }) => {
  const [copied, setCopied] = React.useState<Copied | null>(null);
  const [setupOpen, setSetupOpen] = React.useState(false);
  const actions = React.useMemo<ClaudeActions>(
    () => ({
      copyPrompt: context =>
        navigator.clipboard
          .writeText(claudePrompt(context))
          .then(() =>
            setCopied({ severity: "success", message: "Prompt copied. Paste it into Claude and finish the sentence." })
          )
          .catch(() => setCopied({ severity: "error", message: "Could not copy the prompt." })),
      openSetup: () => setSetupOpen(true),
    }),
    []
  );
  return (
    <ClaudeCtx.Provider value={actions}>
      {children}
      <ClaudeSetup open={setupOpen} onClose={() => setSetupOpen(false)} />
      <Snackbar
        open={!!copied}
        autoHideDuration={6000}
        onClose={() => setCopied(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {copied ? (
          <Alert
            severity={copied.severity}
            onClose={() => setCopied(null)}
            action={
              <Button
                size="small"
                onClick={() => {
                  setCopied(null);
                  setSetupOpen(true);
                }}
              >
                Set up Claude
              </Button>
            }
          >
            {copied.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </ClaudeCtx.Provider>
  );
};

const useClaude = () => {
  const value = React.useContext(ClaudeCtx);
  if (!value) throw new Error("useClaude must be used within ClaudePromptProvider");
  return value;
};

export const useCopyClaudePrompt = () => useClaude().copyPrompt;

export const useOpenClaudeSetup = () => useClaude().openSetup;

export const ClaudePromptButton: React.VFC<{ context: AiContext; tooltip?: string }> = ({
  context,
  tooltip = "Copy a prompt for Claude",
}) => {
  const copyPrompt = useCopyClaudePrompt();
  return (
    <Tooltip title={tooltip}>
      <IconButton size="small" onClick={() => copyPrompt(context)} style={{ padding: 4 }}>
        <span style={{ fontSize: 16 }}>🤖</span>
      </IconButton>
    </Tooltip>
  );
};
